import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import CookieConsent from '@/components/CookieConsent';
import PwaPrompts from '@/components/PwaPrompts';
import WelcomeOverlay from '@/components/WelcomeOverlay';
import { trackPageView } from '@/lib/analytics';
import Home from '@/pages/public/Home';
import About from '@/pages/public/About';
import Services from '@/pages/public/Services';
import SundaySchool from '@/pages/public/SundaySchool';
import SummerCamp from '@/pages/public/SummerCamp';
import PrayerCell from '@/pages/public/PrayerCell';
import Evangelism from '@/pages/public/Evangelism';
import Live from '@/pages/public/Live';
import Contact from '@/pages/public/Contact';
import RSVP from '@/pages/public/RSVP';
import MorningPrayer from '@/pages/public/MorningPrayer';
import VolunteerRegistration from '@/pages/public/VolunteerRegistration';
import Donate from '@/pages/public/Donate';
import DonateSuccess from '@/pages/public/DonateSuccess';
import DonateCancelled from '@/pages/public/DonateCancelled';
import Media from '@/pages/public/Media';
import Leaflets from '@/pages/public/Leaflets';
import Privacy from '@/pages/public/Privacy';
import Safeguarding from '@/pages/public/Safeguarding';
import FAQ from '@/pages/public/FAQ';
import Login from '@/pages/auth/Login';
import AuthCallback from '@/pages/auth/AuthCallback';
import PendingApproval from '@/pages/auth/PendingApproval';
import MemberLayout from '@/components/layout/MemberLayout';
import MemberDashboard from '@/pages/member/Dashboard';
import MemberProfile from '@/pages/member/Profile';
import MemberFamily from '@/pages/member/Family';
import MemberVolunteer from '@/pages/member/Volunteer';
import MemberTasks from '@/pages/member/Tasks';
import MemberRSVPHistory from '@/pages/member/RSVPHistory';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminPortal from '@/pages/admin/AdminPortal';
import TeamsLogin from '@/pages/teams/TeamsLogin';
import TeamsDashboard from '@/pages/teams/TeamsDashboard';
import PastorLogin from '@/pages/pastor/PastorLogin';
import PastorPortal from '@/pages/pastor/PastorPortal';
import { AuthProvider } from '@/context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from '@/context/SiteSettingsContext';

function AppRouter() {
  const location = useLocation();
  const { settings, loading: settingsLoading } = useSiteSettings();

  // Report SPA route changes to Google Analytics (no-op unless consent granted
  // and we're on a real production hostname — see /lib/analytics.js).
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Synchronously catch OAuth callback in URL hash BEFORE other auth checks
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }

  // When the admin has disabled the Donate page we redirect any /donate
  // navigation (including success/cancelled Stripe redirects) back to home.
  // While settings are still loading we render nothing on these routes to
  // avoid a flash of the donate form before the redirect triggers.
  const donateHidden = !settingsLoading && settings.donate_page_visible === false;
  const donateRouteElement = (page) => {
    if (settingsLoading && location.pathname.startsWith('/donate')) return null;
    if (donateHidden) return <Navigate to="/" replace />;
    return page;
  };

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/sunday-school" element={<SundaySchool />} />
        <Route path="/services/summer-camp" element={<SummerCamp />} />
        <Route path="/services/prayer-cell" element={<PrayerCell />} />
        <Route path="/services/evangelism" element={<Evangelism />} />
        <Route path="/live" element={<Live />} />
        <Route path="/morning-prayer" element={<MorningPrayer />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/events/:slug/rsvp" element={<RSVP />} />
        <Route path="/events/:slug/volunteer" element={<VolunteerRegistration />} />
        <Route path="/donate" element={donateRouteElement(<Donate />)} />
        <Route path="/donate/success" element={donateRouteElement(<DonateSuccess />)} />
        <Route path="/donate/cancelled" element={donateRouteElement(<DonateCancelled />)} />
        <Route path="/media" element={<Media />} />
        <Route path="/leaflets" element={<Leaflets />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/safeguarding" element={<Safeguarding />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pending-approval" element={<PendingApproval />} />
      </Route>

      {/* Member portal */}
      <Route element={<MemberLayout />}>
        <Route path="/member/dashboard" element={<MemberDashboard />} />
        <Route path="/member/profile" element={<MemberProfile />} />
        <Route path="/member/family" element={<MemberFamily />} />
        <Route path="/member/volunteer" element={<MemberVolunteer />} />
        <Route path="/member/tasks" element={<MemberTasks />} />
        <Route path="/member/rsvps" element={<MemberRSVPHistory />} />
      </Route>

      {/* Admin portal */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminPortal />} />

      {/* Teams portal */}
      <Route path="/teams" element={<TeamsLogin />} />
      <Route path="/teams/dashboard" element={<TeamsDashboard />} />

      {/* Pastor portal */}
      <Route path="/pastor/login" element={<PastorLogin />} />
      <Route path="/pastor" element={<PastorPortal />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteSettingsProvider>
          <AppRouter />
          <WelcomeOverlay />
          <CookieConsent />
          <PwaPrompts />
        </SiteSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
